import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import axios from "axios";
import { 
  Sparkles, Heart, History, ArrowLeft, 
  Calendar, Play, Clock, TrendingDown, ArrowDown, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import logger from "../lib/logger";
import { AppMenu } from "../components/AppMenu";
import { SiteFooter } from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVENTION_TITLES = {
  quiet_the_worry: "Quiet The Worry",
  overcome_overwhelm: "Overcome Your Overwhelm",
  dial_down_tension: "Dial Down Body Tension",
  healing_old_stories: "Healing Old Stories To Let Them Go",
  beat_stage_fright: "Beat Stage Fright",
  fear_of_sleeplessness: "Fear Of Sleeplessness",
  shake_off_sadness: "Shake Off The Blues",
  boost_self_worth: "Boost Your Self-Worth",
  hold_boundaries: "Hold Your Boundaries",
  relief_from_grief: "Relief From Grief",
  calm_your_anger: "Calm Your Anger",
  enhance_performance: "Enhance Your Performance",
  new_habit_new_you: "New Habit, New You"
};

const INTERVENTION_ICONS = {
  quiet_the_worry: "🧘",
  overcome_overwhelm: "🌊",
  dial_down_tension: "💆",
  healing_old_stories: "📖",
  beat_stage_fright: "🎭",
  fear_of_sleeplessness: "🌙",
  shake_off_sadness: "🌤",
  boost_self_worth: "💪",
  hold_boundaries: "🛡",
  relief_from_grief: "🕊",
  calm_your_anger: "🔥",
  enhance_performance: "🎯",
  new_habit_new_you: "🌱"
};

const HistoryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  // null | "all" | history_id — drives the confirm dialog for both deletes
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // When clearing all history, offer a parallel "also clear in-progress" option
  const [alsoClearProgress, setAlsoClearProgress] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/history`, { withCredentials: true });
      setHistory(response.data);
    } catch (error) {
      logger.error("Failed to fetch history:", error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      if (pendingDelete === "all") {
        const res = await axios.delete(`${API}/history`, { withCredentials: true });
        setHistory([]);

        // Optional parallel cleanup — clear every in-progress session too.
        let progressCleared = 0;
        if (alsoClearProgress) {
          try {
            const progRes = await axios.delete(`${API}/progress`, { withCredentials: true });
            progressCleared = progRes.data?.deleted_count || 0;
          } catch (err) {
            logger.error("Clear progress failed:", err);
          }
        }

        const n = res.data?.deleted_count;
        const base = n ? `Cleared ${n} entr${n === 1 ? "y" : "ies"}` : "History cleared";
        toast.success(
          progressCleared > 0
            ? `${base} · ${progressCleared} in-progress session${progressCleared === 1 ? "" : "s"} removed.`
            : `${base}.`
        );
      } else {
        await axios.delete(`${API}/history/${pendingDelete}`, { withCredentials: true });
        setHistory((prev) => prev.filter((item) => item.history_id !== pendingDelete));
        toast.success("Entry removed.");
      }
      setPendingDelete(null);
      setAlsoClearProgress(false);
    } catch (error) {
      logger.error("Delete failed:", error);
      toast.error("Couldn't delete. Try again in a moment.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3090A8]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="history-page">
      {/* Header */}
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#3090A8] rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-semibold text-xl text-[#1B2349]">Bilateral Calm</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/dashboard" 
              className="text-[#718096] hover:text-[#3090A8] transition-colors"
              data-testid="nav-dashboard"
            >
              Dashboard
            </Link>
            <Link 
              to="/history" 
              className="text-[#1B2349] font-medium hover:text-[#3090A8] transition-colors"
              data-testid="nav-history"
            >
              History
            </Link>
            <Link 
              to="/favorites" 
              className="text-[#718096] hover:text-[#3090A8] transition-colors"
              data-testid="nav-favorites"
            >
              Favorites
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            {user?.picture && (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-8 h-8 rounded-full"
              />
            )}
            <AppMenu />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 pb-24 app-prose">
        {/* Hero banner — calming canopy photo so the practice-history page
            opens with a moment of softness before the list of past sessions. */}
        <div
          className="relative h-44 sm:h-56 md:h-64 -mx-6 sm:mx-0 mb-8 sm:rounded-3xl overflow-hidden bg-cover bg-center shadow-[0_8px_30px_rgba(27,35,73,0.08)]"
          style={{
            backgroundImage:
              "url('/images/dashboard/practice-history-canopy.webp')",
            backgroundPosition: "center 30%",
          }}
          data-testid="history-hero"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#0e1a33]/55 via-[#0e1a33]/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
            <p className="text-white/85 text-xs uppercase tracking-[0.2em] mb-2">
              Look back
            </p>
            <h2 className="font-outfit font-semibold text-white text-2xl sm:text-3xl tracking-tight max-w-xl leading-tight">
              Every practice plants a little light.
            </h2>
          </div>
        </div>

        {/* Back button and title */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
          </button>
          <div className="flex-1">
            <h1 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349]">
              Practice History
            </h1>
            <p className="text-[#718096]">
              {history.length} completed practice{history.length !== 1 ? 's' : ''}
            </p>
          </div>
          {history.length > 0 && (
            <Button
              onClick={() => setPendingDelete("all")}
              variant="outline"
              className="rounded-full border-[#D98880] text-[#D98880] hover:bg-[#D98880] hover:text-white"
              data-testid="clear-history-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear history
            </Button>
          )}
        </div>

        {/* History List */}
        {history.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 text-center">
            <div className="w-16 h-16 bg-[#EAF0F9] rounded-full flex items-center justify-center mx-auto mb-4">
              <History className="w-8 h-8 text-[#78C0D8]" />
            </div>
            <h3 className="font-outfit font-medium text-lg text-[#1B2349] mb-2">
              No History Yet
            </h3>
            <p className="text-[#718096] mb-6">
              Complete your first practice to see it here.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-primary"
              data-testid="start-first-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Start a Practice
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((item, index) => (
              <div
                key={item.history_id}
                className={`bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 animate-fade-in-up stagger-${(index % 5) + 1}`}
                data-testid={`history-item-${item.history_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{INTERVENTION_ICONS[item.intervention_type]}</span>
                    <div>
                      <h3 className="font-outfit font-medium text-lg text-[#1B2349]">
                        {INTERVENTION_TITLES[item.intervention_type] || item.intervention_type}
                      </h3>
                      <div className="flex items-center gap-2 text-[#718096] text-sm mt-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          Completed {format(new Date(item.completed_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => navigate(`/intervention/${item.intervention_type}`)}
                      variant="outline"
                      size="sm"
                      className="rounded-full border-[#3090A8] text-[#3090A8] hover:bg-[#3090A8] hover:text-white"
                      data-testid={`repeat-btn-${item.history_id}`}
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Repeat
                    </Button>
                    <Button
                      onClick={() => setPendingDelete(item.history_id)}
                      variant="ghost"
                      size="icon"
                      className="rounded-full text-[#9CA3AF] hover:text-[#D98880] hover:bg-[#FBEAE8]"
                      aria-label="Delete entry"
                      data-testid={`delete-btn-${item.history_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Distress Level Change */}
                {(item.initial_distress !== null && item.initial_distress !== undefined) && (
                  <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`distress-change-${item.history_id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="w-4 h-4 text-[#78C0D8]" />
                      <span className="text-sm font-medium text-[#1B2349]">Distress Level Change</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#718096]">Before</span>
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-semibold text-white ${
                          item.initial_distress >= 7 ? 'bg-[#D98880]' : 
                          item.initial_distress >= 4 ? 'bg-[#E2B887]' : 'bg-[#3090A8]'
                        }`}>
                          {item.initial_distress}
                        </span>
                      </div>
                      <ArrowDown className="w-4 h-4 text-[#3090A8] rotate-[-90deg]" />
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#718096]">After</span>
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-semibold text-white ${
                          (item.final_distress ?? item.initial_distress) >= 7 ? 'bg-[#D98880]' : 
                          (item.final_distress ?? item.initial_distress) >= 4 ? 'bg-[#E2B887]' : 'bg-[#3090A8]'
                        }`}>
                          {item.final_distress ?? '—'}
                        </span>
                      </div>
                      {item.final_distress !== null && item.final_distress !== undefined && item.initial_distress > item.final_distress && (
                        <span className="text-xs text-[#3090A8] font-medium bg-[#E8F0E7] px-2 py-0.5 rounded-full">
                          -{item.initial_distress - item.final_distress} improvement
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Delete confirmation — shared dialog for single-item and clear-all flows */}
      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
            setAlsoClearProgress(false);
          }
        }}
      >
        <AlertDialogContent className="rounded-2xl" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-outfit">
              {pendingDelete === "all" ? "Clear your history?" : "Delete this entry?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4A5568] leading-relaxed">
              {pendingDelete === "all"
                ? `This removes all ${history.length} completed session${history.length === 1 ? "" : "s"} from your history. Your favourites are untouched. This can't be undone.`
                : "This removes the entry from your history. Your favourites and in-progress sessions are untouched. This can't be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingDelete === "all" && (
            <label
              aria-label="Also clear in-progress sessions"
              className="mt-2 flex items-start gap-3 rounded-xl border border-slate-200 bg-[#FAFBFA] p-3 cursor-pointer hover:bg-[#FBF8F1] transition-colors"
              data-testid="also-clear-progress-label"
            >
              <input
                type="checkbox"
                checked={alsoClearProgress}
                onChange={(e) => setAlsoClearProgress(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#3090A8] focus:ring-[#3090A8]/30"
                data-testid="also-clear-progress-checkbox"
              />
              <div>
                <p className="font-medium text-[#1B2349]">Also clear in-progress sessions</p>
                <p className="text-sm text-[#718096] leading-relaxed mt-0.5">
                  Remove the sessions you haven't finished too. Useful if you're starting over.
                </p>
              </div>
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-cancel-btn">
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-[#D98880] hover:bg-[#C8736B] text-white"
              data-testid="delete-confirm-btn"
            >
              {deleting
                ? "Removing…"
                : pendingDelete === "all"
                  ? "Clear history"
                  : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-3">
        <div className="flex items-center justify-around">
          <Link 
            to="/dashboard"
            className="flex flex-col items-center gap-1 text-[#718096]"
          >
            <Sparkles className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </Link>
          <Link 
            to="/history"
            className="flex flex-col items-center gap-1 text-[#3090A8]"
          >
            <History className="w-5 h-5" />
            <span className="text-xs">History</span>
          </Link>
          <Link 
            to="/favorites"
            className="flex flex-col items-center gap-1 text-[#718096]"
          >
            <Heart className="w-5 h-5" />
            <span className="text-xs">Favorites</span>
          </Link>
        </div>
      </nav>
      <SiteFooter />
    </div>
  );
};

export default HistoryPage;
